import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

export default function InstructorMonthSessions() {
  // ====== Stan UI ======
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [monthIdx, setMonthIdx] = useState(today.getMonth()); // 0..11
  const [selectedMail, setSelectedMail] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // ====== Personel ======
  const [instructors, setInstructors] = useState([]); // [{name, mail}, ...]

  useEffect(() => {
    const ref = collection(db, "personel");
    const unsub = onSnapshot(ref, (snap) => {
      const list = snap.docs
        .map((d) => d.data())
        .filter(Boolean)
        .map((x) => ({
          name: x?.name || x?.mail || "—",
          mail: x?.mail || "",
        }))
        .filter((x) => x.mail);
      // sort po imieniu/nazwisku
      list.sort((a, b) => a.name.localeCompare(b.name, "pl"));
      setInstructors(list);
      // auto-ustaw pierwszy jeśli nic nie wybrane
      if (!selectedMail && list.length > 0) {
        setSelectedMail(list[0].mail);
      }
    });
    return () => unsub();
  }, []); // 1x

  // ====== Granice miesiąca ======
  const pad2 = (n) => String(n).padStart(2, "0");
  const bounds = useMemo(() => {
    const start = `${year}-${pad2(monthIdx + 1)}-01`;
    const nextMonthStart =
      monthIdx === 11 ? `${year + 1}-01-01` : `${year}-${pad2(monthIdx + 2)}-01`;
    const lastDate = new Date(year, monthIdx + 1, 0);
    const endDisplay = `${year}-${pad2(monthIdx + 1)}-${pad2(lastDate.getDate())}`;
    return { start, nextMonthStart, endDisplay };
  }, [year, monthIdx]);

  // ====== Zapytanie do sessions ======
  useEffect(() => {
    const mail = selectedMail?.trim();
    if (!mail) {
      setRows([]);
      return;
    }

    const sessionsRef = collection(db, "sessions");
    const qPrimary = query(
      sessionsRef,
      where("type", "==", "lesson"),
      where("instructors", "array-contains", mail),
      where("date", ">=", bounds.start),
      where("date", "<", bounds.nextMonthStart),
      orderBy("date", "asc"),
      orderBy("startTime", "asc")
    );

    const qFallback = query(
      sessionsRef,
      where("type", "==", "lesson"),
      where("instructors", "array-contains", mail)
    );

    let unsub = () => {};
    setLoading(true);

    const attachFallback = () => {
      unsub = onSnapshot(
        qFallback,
        (snap2) => {
          const list = snap2.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((row) => {
              const v = row?.date;
              if (!v) return false;
              if (typeof v === "string") {
                return v >= bounds.start && v < bounds.nextMonthStart;
              }
              if (v && typeof v.toDate === "function") {
                const iso = v.toDate().toISOString().slice(0, 10);
                return iso >= bounds.start && iso < bounds.nextMonthStart;
              }
              return false;
            })
            .sort((a, b) => {
              const da =
                typeof a.date === "string"
                  ? a.date
                  : a.date?.toDate?.().toISOString().slice(0, 10) || "";
              const db =
                typeof b.date === "string"
                  ? b.date
                  : b.date?.toDate?.().toISOString().slice(0, 10) || "";
              if (da !== db) return da.localeCompare(db);
              return String(a.startTime || "").localeCompare(
                String(b.startTime || "")
              );
            });

          setRows(list);
          setLoading(false);
        },
        () => setLoading(false)
      );
    };

    const attachPrimary = () => {
      unsub = onSnapshot(
        qPrimary,
        (snap) => {
          if (snap.empty) {
            unsub();
            attachFallback();
            return;
          }
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setRows(list);
          setLoading(false);
        },
        () => {
          unsub();
          attachFallback();
        }
      );
    };

    attachPrimary();
    return () => unsub();
  }, [selectedMail, bounds.start, bounds.nextMonthStart]);

  // ====== UI ======
  const monthsPL = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
  ];
  const years = (() => {
    const y = today.getFullYear();
    return [y - 1, y, y + 1];
  })();

  return (
    <div style={{ padding: 16, maxWidth: 900, margin: "0 auto" }}>
      <h2 style={{ marginBottom: 12 }}>Zajęcia instruktora – podgląd miesięczny</h2>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 12, color: "#555" }}>Instruktor</div>
          <select
            value={selectedMail}
            onChange={(e) => setSelectedMail(e.target.value)}
            style={{ padding: "8px 10px", minWidth: 280 }}
          >
            {instructors.length === 0 && (
              <option value="">(ładowanie…)</option>
            )}
            {instructors.map((i) => (
              <option key={i.mail} value={i.mail}>
                {i.name} — {i.mail}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 12, color: "#555" }}>Rok</div>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ padding: "8px 10px" }}>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 12, color: "#555" }}>Miesiąc</div>
          <select value={monthIdx} onChange={(e) => setMonthIdx(Number(e.target.value))} style={{ padding: "8px 10px" }}>
            {monthsPL.map((m, idx) => (
              <option key={m} value={idx}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 13, color: "#333" }}>
        Zakres: <strong>{bounds.start}</strong> – <strong>{bounds.endDisplay}</strong>{" "}
        (zapytanie używa <code>[{bounds.start}, {bounds.nextMonthStart})</code>)
      </div>

      <hr style={{ margin: "14px 0" }} />

      {loading ? (
        <div>Ładowanie…</div>
      ) : rows.length === 0 ? (
        <div>Brak zajęć w tym miesiącu.</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {rows.map((s) => {
            const d =
              typeof s.date === "string"
                ? s.date
                : s.date?.toDate?.().toISOString().slice(0, 10) || "-";
            return (
              <div
                key={s.id}
                style={{
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: 6,
                  padding: 10,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <div
                    style={{
                      width: 6,
                      height: 36,
                      background: s.color || "#666",
                      borderRadius: 6,
                    }}
                  />
                  <div style={{ display: "grid" }}>
                    <div style={{ fontWeight: 700 }}>
                      {d} • {s.startTime}–{s.endTime}
                    </div>
                    <div style={{ fontSize: 13, color: "#444" }}>
                      {s.location || "—"} — {s.name || "Bez nazwy"}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  {Array.isArray(s.instructors) ? s.instructors[0] : ""}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 16, fontSize: 12, color: "#666" }}>
        <strong>Indeks Firestore:</strong> jeśli dostaniesz błąd braku indeksu (array-contains + range + orderBy),
        dodaj z polami: <code>type ==</code>, <code>instructors array-contains</code>, <code>date asc</code>, <code>startTime asc</code>.
      </div>
    </div>
  );
}
